"use client";

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { Loader } from '@googlemaps/js-api-loader';

interface GoogleMapsContextType {
  isLoaded: boolean;
  loadError: Error | null;
  google: typeof google | null;
  waitForGoogle: () => Promise<typeof google>;
}

const GoogleMapsContext = createContext<GoogleMapsContextType | undefined>(undefined);

export function GoogleMapsProvider({ children }: { children: ReactNode }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [google, setGoogle] = useState<typeof window.google | null>(null);
  const [initPromise, setInitPromise] = useState<Promise<typeof google> | null>(null);

  const waitForGoogle = useCallback(() => {
    if (google) return Promise.resolve(google);
    if (initPromise) return initPromise;

    const promise = new Promise<typeof google>((resolve, reject) => {
      const loader = new Loader({
        apiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY!,
        version: "weekly",
        libraries: ["drawing", "places", "geometry"]
      });

      loader.load()
        .then(() => {
          setGoogle(window.google);
          setIsLoaded(true);
          resolve(window.google);
        })
        .catch((err) => {
          console.error('Error loading Google Maps:', err);
          setLoadError(err);
          reject(err);
        });
    });

    setInitPromise(promise);
    return promise;
  }, [google, initPromise]);

  useEffect(() => {
    waitForGoogle();
  }, [waitForGoogle]);

  const value = {
    isLoaded,
    loadError,
    google,
    waitForGoogle
  };

  return (
    <GoogleMapsContext.Provider value={value}>
      {children}
    </GoogleMapsContext.Provider>
  );
}

export function useGoogleMaps() {
  const context = useContext(GoogleMapsContext);
  if (!context) {
    throw new Error('useGoogleMaps must be used within a GoogleMapsProvider');
  }
  return context;
}